import React, { ReactNode } from 'react';
import { Transition } from 'react-transition-group';

interface SlideComponentProps {
  children: ReactNode; // Accepts React children
  in: boolean; // Controls the visibility of the component
  duration?: number; // Optional: Duration of the animation
  maxHeight?: string; // Optional: Max height for the sliding animation
}

/**
 * Component that slides in/out its children when the `in` prop changes.
 * 
 * Uses the `Transition` component from `react-transition-group` to animate the sliding effect.
 * 
 */
export const SlideComponent: React.FC<SlideComponentProps> = ({
  children,
  in: inProp,
  duration = 500,
  maxHeight = '200px',
}) => {
  // Initial style for the sliding element
  const defaultStyle = {
    transition: `max-height ${duration}ms ease-in-out, opacity ${duration}ms ease-in-out`,
    overflow: 'hidden',
    opacity: 0,
    maxHeight: 0, // Start with 0 height
  };

  // Styles for each transition state
  const transitionStyles: { [id: string]: React.CSSProperties } = {
    entering: { maxHeight: maxHeight, opacity: 1 },
    entered: { maxHeight: maxHeight, opacity: 1 },
    exiting: { maxHeight: 0, opacity: 0 },
    exited: { maxHeight: 0, opacity: 0 },
  };

  return (
    <Transition in={inProp} timeout={duration} unmountOnExit>
      {(state) => (
        <div
          style={{
            ...defaultStyle,
            ...transitionStyles[state],
          }}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};
